import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  TrendingUp,
  Package,
  Users,
  UserCog,
  AlertTriangle,
  DollarSign,
  ShoppingCart,
  Calendar,
  Filter,
  RefreshCw,
  Box,
  Layers,
  Hash,
  FileText,
  CreditCard,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Settings,
  Percent,
  Bell,
  Clock,
  AlertCircle,
  XCircle,
  CheckCircle,
  X,
  Banknote,
  History,
  Plus,
  Trash2,
  Phone,
  MessageCircle,
  MapPin,
  Wallet
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4'];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
};

export const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dados, setDados] = useState(null);
  const navigate = useNavigate();
  const [periodo, setPeriodo] = useState(() => {
    // Default: últimos 30 dias — evita ficar tudo zerado no dia 1 do mês.
    const hoje = new Date();
    const inicio = new Date(hoje);
    inicio.setDate(hoje.getDate() - 30);
    return {
      dataInicio: inicio.toISOString().split('T')[0],
      dataFim: hoje.toISOString().split('T')[0],
    };
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [tipoDetalhe, setTipoDetalhe] = useState(null); // Para mostrar detalhes de um tipo específico
  
  // Configurações de visibilidade das seções
  const [secoes, setSecoes] = useState({
    resumo: true,
    tiposUnidade: true,
    formaPagamento: true,
    evolucaoDiaria: true,
    vendedores: true,
    clientes: true,
    produtos: true,
    comissoes: true,
    alertas: true
  });

  // Estado para alertas de pagamento
  const [alertas, setAlertas] = useState(null);
  const [alertaDetalhe, setAlertaDetalhe] = useState(null);
  // Filtros por urgência (badges clicáveis nos alertas)
  const [filtroAlertaCliente, setFiltroAlertaCliente] = useState(null); // 'urgente_atrasado', '5_dias_atrasado', etc.
  const [filtroAlertaComissao, setFiltroAlertaComissao] = useState(null);
  const [showPagamentoModal, setShowPagamentoModal] = useState(false);
  const [pagamentoData, setPagamentoData] = useState({ valor: '', data: '', observacao: '' });
  const [alertaSelecionado, setAlertaSelecionado] = useState(null);
  const [abatimentos, setAbatimentos] = useState([{ valor: '', descricao: '' }]);

  // Novos estados para devedores e info de romaneios no Dashboard
  const [allRomaneios, setAllRomaneios] = useState([]);

  // Filtros do painel "Vencidos do Mês"
  const [vencidosFiltro, setVencidosFiltro] = useState({
    dataInicio: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    dataFim: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
    cliente: '',
    valorMin: '',
    valorMax: '',
    prazo: '', // '' (todos), 'boleto', 'pix', 'dinheiro', 'a_prazo', 'parcelado'
    faixaAtraso: '', // '', '1-7', '8-15', '16-30', '30+'
    ordenar: 'atraso_desc', // 'atraso_desc' | 'atraso_asc' | 'valor_desc' | 'cliente'
  });
  const [vencidosPayLoading, setVencidosPayLoading] = useState({});

  // Modal de Pagamento / Nota do painel Vencidos
  const [pagVencidoModal, setPagVencidoModal] = useState({ open: false, romaneio: null });
  const [pagVencidoForm, setPagVencidoForm] = useState({
    valor: '',
    data: new Date().toISOString().slice(0, 10),
    observacao: '',
    forma: 'pix',
    pagarTudo: false,
  });
  const [pagVencidoBusy, setPagVencidoBusy] = useState(false);

  // Funções para gerenciar abatimentos
  const addAbatimento = () => {
    setAbatimentos([...abatimentos, { valor: '', descricao: '' }]);
  };

  const removeAbatimento = (index) => {
    if (abatimentos.length > 1) {
      setAbatimentos(abatimentos.filter((_, i) => i !== index));
    }
  };

  const updateAbatimento = (index, field, value) => {
    const newAbatimentos = [...abatimentos];
    newAbatimentos[index][field] = value;
    setAbatimentos(newAbatimentos);
  };

  const totalAbatimentos = abatimentos.reduce((sum, a) => sum + (parseFloat(a.valor) || 0), 0);

  useEffect(() => {
    fetchDados();
    fetchAlertas();
  }, [periodo]);

  // Auto-refresh dos alertas a cada 30 segundos quando a página está visível
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchAlertas();
        fetchDados();
      }
    };

    // Polling automático a cada 30 segundos
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchAlertas();
      }
    }, 30000);

    // Re-fetch quando a página volta a ficar visível
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchDados = async () => {
    setLoading(true);
    try {
      const [dashResp, romsResp] = await Promise.all([
        axios.get(`${API}/dashboard/detalhado`, {
          params: { data_inicio: periodo.dataInicio, data_fim: periodo.dataFim }
        }),
        axios.get(`${API}/romaneios`).catch(() => ({ data: [] }))
      ]);
      setDados(dashResp.data);
      setAllRomaneios(Array.isArray(romsResp.data) ? romsResp.data : []);
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      toast.error('Erro ao carregar dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchAlertas = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/alertas-pagamento`);
      setAlertas(response.data);
    } catch (error) {
      console.error('Erro ao buscar alertas:', error);
    }
  };

  // Abre o modal de pagamento/nota a partir de uma linha do painel Vencidos
  const abrirPagamentoVencido = (romaneio) => {
    const restante = Math.max(0, (romaneio.valor_total || 0) - (romaneio.valor_pago || 0));
    setPagVencidoModal({ open: true, romaneio });
    setPagVencidoForm({
      valor: restante.toFixed(2).replace('.', ','),
      data: new Date().toISOString().slice(0, 10),
      observacao: '',
      forma: 'pix',
      pagarTudo: true,
    });
  };

  const fecharPagamentoVencido = () => {
    if (pagVencidoBusy) return;
    setPagVencidoModal({ open: false, romaneio: null });
  };

  // Registra pagamento (parcial ou total). Se valor >= saldo, marca pago automaticamente
  const submitPagamentoVencido = async () => {
    const rom = pagVencidoModal.romaneio;
    if (!rom) return;
    const valorNum = parseFloat(String(pagVencidoForm.valor).replace(',', '.')) || 0;
    if (valorNum <= 0) {
      toast.error('Informe um valor de pagamento válido');
      return;
    }
    const saldoAtual = Math.max(0, (rom.valor_total || 0) - (rom.valor_pago || 0));
    if (valorNum > saldoAtual + 0.01) {
      const ok = window.confirm(
        `Valor informado (${formatCurrency(valorNum)}) é maior que o saldo devedor (${formatCurrency(saldoAtual)}).\n\nDeseja continuar mesmo assim?`
      );
      if (!ok) return;
    }
    setPagVencidoBusy(true);
    try {
      const obs = [
        pagVencidoForm.observacao?.trim(),
        `Forma: ${pagVencidoForm.forma}`,
      ].filter(Boolean).join(' | ');
      const resp = await axios.post(`${API}/romaneios/${rom.id}/registrar-pagamento`, {
        valor_pago: valorNum,
        data_pagamento: pagVencidoForm.data,
        observacao: obs || null
      });
      const data = resp.data || {};
      const novoStatus = data.status || (valorNum >= saldoAtual ? 'pago' : 'parcial');
      const novoPago = data.valor_pago != null ? data.valor_pago : ((rom.valor_pago || 0) + valorNum);
      setAllRomaneios(prev => prev.map(r => r.id === rom.id
        ? {
            ...r,
            valor_pago: novoPago,
            status_pagamento: novoStatus,
            forma_pagamento: novoStatus === 'pago' ? 'pago' : r.forma_pagamento,
            historico_pagamentos: [
              ...(r.historico_pagamentos || []),
              { valor: valorNum, data: pagVencidoForm.data, observacao: obs, registrado_em: new Date().toISOString() }
            ]
          }
        : r));
      if (novoStatus === 'pago') {
        toast.success(`Romaneio #${rom.numero_pedido} QUITADO`);
      } else {
        const restante = Math.max(0, (rom.valor_total || 0) - novoPago);
        toast.success(`Pagamento de ${formatCurrency(valorNum)} registrado. Saldo: ${formatCurrency(restante)}`);
      }
      fecharPagamentoVencido();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Erro ao registrar pagamento');
    } finally {
      setPagVencidoBusy(false);
    }
  };

  // Reverter para pendente: remove TODOS os pagamentos do romaneio (volta a "atrasado" se vencido)
  const reverterPagamentos = async () => {
    const rom = pagVencidoModal.romaneio;
    if (!rom) return;
    const historico = rom.historico_pagamentos || [];
    if (historico.length === 0 && (rom.valor_pago || 0) <= 0.01) {
      toast.info('Este romaneio já está pendente / sem pagamentos');
      return;
    }
    const ok = window.confirm(
      `Reverter TODOS os pagamentos de #${rom.numero_pedido}?\n\n` +
      `Isso vai zerar o valor pago e mudar o status para PENDENTE/ATRASADO.\n` +
      `Pagamentos no histórico (${historico.length}) serão removidos.`
    );
    if (!ok) return;
    setPagVencidoBusy(true);
    try {
      // Remove cada pagamento do histórico (índice 0 sempre, pois o array encolhe)
      for (let i = 0; i < historico.length; i++) {
        // eslint-disable-next-line no-await-in-loop
        await axios.delete(`${API}/romaneios/${rom.id}/pagamento/0`);
      }
      setAllRomaneios(prev => prev.map(r => r.id === rom.id
        ? { ...r, valor_pago: 0, status_pagamento: 'pendente', forma_pagamento: r.forma_pagamento === 'pago' ? 'a_receber' : r.forma_pagamento, historico_pagamentos: [] }
        : r));
      toast.success(`Romaneio #${rom.numero_pedido} revertido para pendente`);
      fecharPagamentoVencido();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Erro ao reverter pagamentos');
    } finally {
      setPagVencidoBusy(false);
    }
  };

  const exportarVencidosCSV = (rows) => {
    if (!rows.length) { toast.info('Nenhum vencido para exportar'); return; }
    const header = ['Pedido', 'Cliente', 'Venceu em', 'Dias atraso', 'Forma', 'Valor total', 'Valor pago', 'Saldo devedor'];
    const csv = [header.join(';')].concat(
      rows.map(r => [
        r.numero_pedido || '',
        (r.cliente_nome || '').replace(/;/g, ','),
        r._vencimentoStr,
        r._diasAtraso,
        r.forma_pagamento || '',
        (r.valor_total || 0).toFixed(2).replace('.', ','),
        (r.valor_pago || 0).toFixed(2).replace('.', ','),
        r._saldo.toFixed(2).replace('.', ','),
      ].join(';'))
    ).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vencidos-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const registrarPagamento = async () => {
    if (!alertaSelecionado || !pagamentoData.valor || !pagamentoData.data) {
      toast.error('Preencha todos os campos');
      return;
    }
    
    // Calcular valor líquido (valor - abatimentos)
    const valorBruto = parseFloat(pagamentoData.valor) || 0;
    const valorLiquido = Math.max(0, valorBruto - totalAbatimentos);
    
    // Montar observação com abatimentos se houver
    let observacaoFinal = pagamentoData.observacao || '';
    const abatimentosValidos = abatimentos.filter(a => parseFloat(a.valor) > 0);
    
    if (abatimentosValidos.length > 0) {
      const abatimentosInfo = abatimentosValidos.map(a => 
        `R$ ${parseFloat(a.valor).toFixed(2)}${a.descricao ? ` (${a.descricao})` : ''}`
      ).join(', ');
      const abatimentoTexto = `[Abatimentos: ${abatimentosInfo} = Total: R$ ${totalAbatimentos.toFixed(2)}]`;
      observacaoFinal = observacaoFinal ? `${observacaoFinal} | ${abatimentoTexto}` : abatimentoTexto;
    }
    
    try {
      const endpoint = alertaSelecionado.tipo === 'comissao' 
        ? `${API}/romaneios/${alertaSelecionado.romaneio_id || alertaSelecionado.id}/registrar-comissao`
        : `${API}/romaneios/${alertaSelecionado.id}/registrar-pagamento`;
      
      await axios.post(endpoint, {
        valor_pago: valorLiquido,
        data_pagamento: pagamentoData.data,
        observacao: observacaoFinal
      });
      
      toast.success(`Pagamento registrado! Valor líquido: ${formatCurrency(valorLiquido)}`);
      setShowPagamentoModal(false);
      setPagamentoData({ valor: '', data: '', observacao: '' });
      setAbatimentos([{ valor: '', descricao: '' }]);
      setAlertaSelecionado(null);
      fetchAlertas();
    } catch (error) {
      console.error('Erro ao registrar pagamento:', error);
      toast.error('Erro ao registrar pagamento');
    }
  };

  const toggleSecao = (secao) => {
    setSecoes(prev => ({ ...prev, [secao]: !prev[secao] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-muted-foreground">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  // Preparar dados para gráficos
  const dadosTipos = dados?.vendas_por_tipo ? Object.entries(dados.vendas_por_tipo)
    .map(([tipo, data]) => ({
      name: tipo.charAt(0).toUpperCase() + tipo.slice(1),
      tipo: tipo,
      valor: data.valor,
      quantidade: data.quantidade,
      itens: data.itens
    }))
    .filter(d => d.valor > 0) : [];

  const dadosDiarios = dados?.vendas_por_dia ? Object.entries(dados.vendas_por_dia).map(([data, valores]) => ({
    data: new Date(data).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
    valor: valores.valor,
    romaneios: valores.romaneios
  })) : [];

  // Calcular totais de comissão
  const totalComissaoDevida = dados?.top_10_vendedores?.reduce((acc, v) => acc + (v.comissao_devida || 0), 0) || 0;

  // Produtos vendidos por tipo
  const produtosPorTipo = (tipo) => {
    if (!dados?.detalhes_produtos) return [];
    return Object.entries(dados.detalhes_produtos)
      .filter(([_, data]) => data.por_tipo[tipo] > 0)
      .map(([nome, data]) => ({
        nome,
        valor: data.por_tipo[tipo],
        quantidade: data.qtd_por_tipo[tipo]
      }))
      .sort((a, b) => b.valor - a.valor);
  };

  return (
    <div className="space-y-4" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground">Dashboard de Vendas</h2>
          <p className="text-sm text-muted-foreground">
            Período: {new Date(periodo.dataInicio).toLocaleDateString('pt-BR')} a {new Date(periodo.dataFim).toLocaleDateString('pt-BR')}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <button onClick={() => setShowConfig(!showConfig)} className="btn-secondary p-2" title="Configurar Seções">
            <Settings className="w-4 h-4" />
          </button>
          <button onClick={() => setShowFilters(!showFilters)} className="btn-secondary flex items-center gap-2 text-sm">
            <Filter className="w-4 h-4" />
            Filtros
          </button>
          <button onClick={fetchDados} className="btn-primary flex items-center gap-2 text-sm">
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </button>
        </div>
      </div>

      {/* Configurações de Seções */}
      {showConfig && (
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Settings className="w-4 h-4" /> Personalizar Dashboard
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {Object.entries(secoes).map(([key, value]) => (
              <label key={key} className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-accent">
                <input
                  type="checkbox"
                  checked={value}
                  onChange={() => toggleSecao(key)}
                  className="rounded"
                />
                <span className="text-sm capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Filtros */}
      {showFilters && (
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-xs font-medium mb-1">Data Início</label>
              <input type="date" value={periodo.dataInicio} onChange={(e) => setPeriodo(prev => ({ ...prev, dataInicio: e.target.value }))} className="input-field text-sm h-9" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Data Fim</label>
              <input type="date" value={periodo.dataFim} onChange={(e) => setPeriodo(prev => ({ ...prev, dataFim: e.target.value }))} className="input-field text-sm h-9" />
            </div>
            <button onClick={() => {
              const hoje = new Date();
              setPeriodo({ dataInicio: new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().split('T')[0], dataFim: hoje.toISOString().split('T')[0] });
            }} className="btn-secondary text-xs h-9">Este Mês</button>
            <button onClick={() => {
              const hoje = new Date();
              setPeriodo({ dataInicio: new Date(hoje.getFullYear(), 0, 1).toISOString().split('T')[0], dataFim: hoje.toISOString().split('T')[0] });
            }} className="btn-secondary text-xs h-9">Este Ano</button>
            <button onClick={() => {
              setPeriodo({ dataInicio: '2020-01-01', dataFim: new Date().toISOString().split('T')[0] });
            }} className="btn-secondary text-xs h-9">Todo Período</button>
          </div>
        </div>
      )}

      {/* Cards Resumo Principal */}
      {secoes.resumo && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-3 text-white">
            <DollarSign className="w-6 h-6 opacity-80" />
            <p className="text-xl font-bold mt-1">{formatCurrency(dados?.resumo?.total_vendas)}</p>
            <p className="text-xs opacity-80">Total Vendas</p>
          </div>
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-3 text-white">
            <FileText className="w-6 h-6 opacity-80" />
            <p className="text-xl font-bold mt-1">{dados?.resumo?.total_romaneios || 0}</p>
            <p className="text-xs opacity-80">Romaneios</p>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-3 text-white">
            <TrendingUp className="w-6 h-6 opacity-80" />
            <p className="text-xl font-bold mt-1">{formatCurrency(dados?.resumo?.ticket_medio)}</p>
            <p className="text-xs opacity-80">Ticket Médio</p>
          </div>
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-3 text-white">
            <Percent className="w-6 h-6 opacity-80" />
            <p className="text-xl font-bold mt-1">{formatCurrency(totalComissaoDevida)}</p>
            <p className="text-xs opacity-80">Comissões</p>
          </div>
          <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-lg p-3 text-white">
            <Users className="w-6 h-6 opacity-80" />
            <p className="text-xl font-bold mt-1">{dados?.resumo?.total_clientes || 0}</p>
            <p className="text-xs opacity-80">Clientes</p>
          </div>
          <div className="bg-gradient-to-br from-pink-500 to-pink-600 rounded-lg p-3 text-white">
            <Package className="w-6 h-6 opacity-80" />
            <p className="text-xl font-bold mt-1">{dados?.resumo?.total_produtos || 0}</p>
            <p className="text-xs opacity-80">Produtos</p>
          </div>
          {(() => {
            const orcamentos = (allRomaneios || []).filter(r => r.is_orcamento);
            const total = orcamentos.reduce((s, r) => s + (r.valor_total || 0), 0);
            return (
              <button
                onClick={() => navigate('/dashboardadminromaneio/romaneios?tipo=orcamentos')}
                className="col-span-2 md:col-span-1 lg:col-span-2 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-lg p-3 text-white text-left hover:shadow-lg transition-shadow"
                data-testid="card-orcamentos-pendentes"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-wide opacity-90 font-semibold">📋 Orçamentos Pendentes</p>
                    <p className="text-2xl font-bold mt-1">{formatCurrency(total)}</p>
                    <p className="text-xs opacity-90 mt-0.5">{orcamentos.length} orçamento{orcamentos.length !== 1 ? 's' : ''} aguardando conversão</p>
                  </div>
                  <FileText className="w-6 h-6 opacity-80" />
                </div>
              </button>
            );
          })()}
        </div>
      )}

      {/* ============ ALERTAS DE PAGAMENTO E COMISSÕES ============ */}
      {secoes.alertas && alertas && (alertas.resumo_clientes?.total > 0 || alertas.resumo_comissoes?.total > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Card Alertas de Clientes */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="p-4 border-b border-border bg-gradient-to-r from-red-500/10 to-orange-500/10">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold flex items-center gap-2">
                  <Bell className="w-5 h-5 text-red-500" />
                  Alertas de Pagamento - Clientes
                </h3>
                <span className="text-sm font-bold text-red-500">
                  {formatCurrency(alertas.resumo_clientes?.valor_total_pendente)}
                </span>
              </div>
              
              {/* Badges de resumo (clicáveis — filtram a lista abaixo) */}
              <div className="flex flex-wrap gap-2 mt-3" data-testid="alertas-clientes-filtros">
                {alertas.resumo_clientes?.urgente_atrasado > 0 && (
                  <button
                    type="button"
                    onClick={() => setFiltroAlertaCliente(f => f === 'urgente_atrasado' ? null : 'urgente_atrasado')}
                    className={`px-2 py-1 rounded-full text-xs font-bold bg-red-600 text-white transition hover:opacity-90 ${filtroAlertaCliente === 'urgente_atrasado' ? 'ring-2 ring-offset-1 ring-red-300' : filtroAlertaCliente ? 'opacity-40' : 'animate-pulse'}`}
                    data-testid="filtro-cliente-urgente_atrasado"
                  >
                    🔥 {alertas.resumo_clientes.urgente_atrasado} URGENTE!
                  </button>
                )}
                {alertas.resumo_clientes?.['5_dias_atrasado'] > 0 && (
                  <button
                    type="button"
                    onClick={() => setFiltroAlertaCliente(f => f === '5_dias_atrasado' ? null : '5_dias_atrasado')}
                    className={`px-2 py-1 rounded-full text-xs font-medium bg-red-500 text-white transition hover:opacity-90 ${filtroAlertaCliente === '5_dias_atrasado' ? 'ring-2 ring-offset-1 ring-red-300' : filtroAlertaCliente ? 'opacity-40' : ''}`}
                    data-testid="filtro-cliente-5_dias_atrasado"
                  >
                    {alertas.resumo_clientes['5_dias_atrasado']} +5d atrasado
                  </button>
                )}
                {alertas.resumo_clientes?.['1_dia_atrasado'] > 0 && (
                  <button
                    type="button"
                    onClick={() => setFiltroAlertaCliente(f => f === '1_dia_atrasado' ? null : '1_dia_atrasado')}
                    className={`px-2 py-1 rounded-full text-xs font-medium bg-orange-500 text-white transition hover:opacity-90 ${filtroAlertaCliente === '1_dia_atrasado' ? 'ring-2 ring-offset-1 ring-orange-300' : filtroAlertaCliente ? 'opacity-40' : ''}`}
                    data-testid="filtro-cliente-1_dia_atrasado"
                  >
                    {alertas.resumo_clientes['1_dia_atrasado']} 1d atrasado
                  </button>
                )}
                {alertas.resumo_clientes?.['1_dia_vencer'] > 0 && (
                  <button
                    type="button"
                    onClick={() => setFiltroAlertaCliente(f => f === '1_dia_vencer' ? null : '1_dia_vencer')}
                    className={`px-2 py-1 rounded-full text-xs font-medium bg-yellow-500 text-black transition hover:opacity-90 ${filtroAlertaCliente === '1_dia_vencer' ? 'ring-2 ring-offset-1 ring-yellow-300' : filtroAlertaCliente ? 'opacity-40' : ''}`}
                    data-testid="filtro-cliente-1_dia_vencer"
                  >
                    {alertas.resumo_clientes['1_dia_vencer']} vence amanhã
                  </button>
                )}
                {alertas.resumo_clientes?.['5_dias_vencer'] > 0 && (
                  <button
                    type="button"
                    onClick={() => setFiltroAlertaCliente(f => f === '5_dias_vencer' ? null : '5_dias_vencer')}
                    className={`px-2 py-1 rounded-full text-xs font-medium bg-yellow-400 text-black transition hover:opacity-90 ${filtroAlertaCliente === '5_dias_vencer' ? 'ring-2 ring-offset-1 ring-yellow-300' : filtroAlertaCliente ? 'opacity-40' : ''}`}
                    data-testid="filtro-cliente-5_dias_vencer"
                  >
                    {alertas.resumo_clientes['5_dias_vencer']} em 5 dias
                  </button>
                )}
                {alertas.resumo_clientes?.['10_dias_vencer'] > 0 && (
                  <button
                    type="button"
                    onClick={() => setFiltroAlertaCliente(f => f === '10_dias_vencer' ? null : '10_dias_vencer')}
                    className={`px-2 py-1 rounded-full text-xs font-medium bg-green-400 text-black transition hover:opacity-90 ${filtroAlertaCliente === '10_dias_vencer' ? 'ring-2 ring-offset-1 ring-green-300' : filtroAlertaCliente ? 'opacity-40' : ''}`}
                    data-testid="filtro-cliente-10_dias_vencer"
                  >
                    {alertas.resumo_clientes['10_dias_vencer']} em 10 dias
                  </button>
                )}
                {filtroAlertaCliente && (
                  <button
                    type="button"
                    onClick={() => setFiltroAlertaCliente(null)}
                    className="px-2 py-1 rounded-full text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                    data-testid="filtro-cliente-limpar"
                  >
                    ✕ Limpar
                  </button>
                )}
              </div>
            </div>
            
            {/* Lista de alertas (filtrada) */}
            <div className="max-h-[400px] overflow-y-auto divide-y divide-border" data-testid="lista-alertas-clientes">
              {(() => {
                const lista = (alertas.alertas_clientes || []).filter(a => !filtroAlertaCliente || a.urgencia === filtroAlertaCliente);
                if (lista.length === 0) {
                  return (
                    <p className="text-center text-sm text-muted-foreground py-8 italic">
                      Nenhum alerta {filtroAlertaCliente ? 'nesse filtro' : ''}.
                    </p>
                  );
                }
                return lista.map((alerta) => {
                const diasLabel = alerta.dias_ate_vencimento < 0
                  ? `${Math.abs(alerta.dias_ate_vencimento)} dia${Math.abs(alerta.dias_ate_vencimento) !== 1 ? 's' : ''} atrasado`
                  : alerta.dias_ate_vencimento === 0
                    ? 'vence hoje'
                    : `${alerta.dias_ate_vencimento} dia${alerta.dias_ate_vencimento !== 1 ? 's' : ''} restante${alerta.dias_ate_vencimento !== 1 ? 's' : ''}`;
                const vencFmt = alerta.data_vencimento ? new Date(alerta.data_vencimento).toLocaleDateString('pt-BR') : '-';
                const whatsappUrl = alerta.cliente_whatsapp
                  ? `https://wa.me/${alerta.cliente_whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(`Olá ${alerta.cliente_nome}, sobre o pedido ${alerta.numero_pedido} de ${formatCurrency(alerta.valor_pendente)} — vencimento em ${vencFmt}.`)}`
                  : null;
                return (
                <div
                  key={alerta.id}
                  className={`p-3 transition-all ${alerta.urgencia === 'urgente_atrasado' ? 'bg-red-500/10' : ''}`}
                  data-testid={`alerta-cliente-${alerta.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2 min-w-0 flex-1 cursor-pointer" onClick={() => setAlertaDetalhe(alerta)}>
                      {alerta.urgencia === 'urgente_atrasado' && <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />}
                      {alerta.urgencia.includes('atrasado') && alerta.urgencia !== 'urgente_atrasado' && <AlertCircle className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />}
                      {alerta.urgencia.includes('vencer') && <Clock className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">{alerta.cliente_nome}</p>
                        <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                          <span>Pedido: <b>{alerta.numero_pedido}</b></span>
                          <span>Venc: <b>{vencFmt}</b></span>
                          {alerta.vendedor_nome && alerta.vendedor_nome !== '-' && <span>Vend: {alerta.vendedor_nome}</span>}
                          {alerta.cliente_cidade && <span className="inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" />{alerta.cliente_cidade}</span>}
                          {alerta.forma_pagamento && <span className="inline-flex items-center gap-0.5"><Wallet className="w-3 h-3" />{alerta.forma_pagamento}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-bold text-sm" style={{ color: alerta.cor }}>{formatCurrency(alerta.valor_pendente)}</p>
                      <p className="text-[11px] font-medium" style={{ color: alerta.cor }}>{diasLabel}</p>
                    </div>
                  </div>
                  {(alerta.cliente_telefone || alerta.cliente_whatsapp) && (
                    <div className="flex items-center gap-2 mt-1.5 pl-7">
                      {alerta.cliente_telefone && (
                        <a href={`tel:${alerta.cliente_telefone}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline" data-testid={`alerta-tel-${alerta.id}`}>
                          <Phone className="w-3 h-3" /> {alerta.cliente_telefone}
                        </a>
                      )}
                      {whatsappUrl && (
                        <a href={whatsappUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-[11px] text-emerald-600 hover:underline" data-testid={`alerta-wpp-${alerta.id}`}>
                          <MessageCircle className="w-3 h-3" /> WhatsApp
                        </a>
                      )}
                    </div>
                  )}
                </div>
                );});
              })()}
            </div>
          </div>

          {/* Card Alertas de Comissões */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="p-4 border-b border-border bg-gradient-to-r from-purple-500/10 to-blue-500/10">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold flex items-center gap-2">
                  <Banknote className="w-5 h-5 text-purple-500" />
                  Comissões Pendentes - Vendedores
                </h3>
                <span className="text-sm font-bold text-purple-500">
                  {formatCurrency(alertas.resumo_comissoes?.valor_total_pendente)}
                </span>
              </div>
              
              {/* Badges de resumo (clicáveis — filtram a lista abaixo) */}
              <div className="flex flex-wrap gap-2 mt-3" data-testid="alertas-comissoes-filtros">
                {alertas.resumo_comissoes?.urgente_atrasado > 0 && (
                  <button
                    type="button"
                    onClick={() => setFiltroAlertaComissao(f => f === 'urgente_atrasado' ? null : 'urgente_atrasado')}
                    className={`px-2 py-1 rounded-full text-xs font-bold bg-purple-600 text-white transition hover:opacity-90 ${filtroAlertaComissao === 'urgente_atrasado' ? 'ring-2 ring-offset-1 ring-purple-300' : filtroAlertaComissao ? 'opacity-40' : 'animate-pulse'}`}
                    data-testid="filtro-comissao-urgente_atrasado"
                  >
                    🔥 {alertas.resumo_comissoes.urgente_atrasado} URGENTE!
                  </button>
                )}
                {alertas.resumo_comissoes?.['5_dias_atrasado'] > 0 && (
                  <button
                    type="button"
                    onClick={() => setFiltroAlertaComissao(f => f === '5_dias_atrasado' ? null : '5_dias_atrasado')}
                    className={`px-2 py-1 rounded-full text-xs font-medium bg-purple-500 text-white transition hover:opacity-90 ${filtroAlertaComissao === '5_dias_atrasado' ? 'ring-2 ring-offset-1 ring-purple-300' : filtroAlertaComissao ? 'opacity-40' : ''}`}
                    data-testid="filtro-comissao-5_dias_atrasado"
                  >
                    {alertas.resumo_comissoes['5_dias_atrasado']} +5d atrasado
                  </button>
                )}
                {alertas.resumo_comissoes?.['1_dia_atrasado'] > 0 && (
                  <button
                    type="button"
                    onClick={() => setFiltroAlertaComissao(f => f === '1_dia_atrasado' ? null : '1_dia_atrasado')}
                    className={`px-2 py-1 rounded-full text-xs font-medium bg-purple-400 text-white transition hover:opacity-90 ${filtroAlertaComissao === '1_dia_atrasado' ? 'ring-2 ring-offset-1 ring-purple-300' : filtroAlertaComissao ? 'opacity-40' : ''}`}
                    data-testid="filtro-comissao-1_dia_atrasado"
                  >
                    {alertas.resumo_comissoes['1_dia_atrasado']} 1d atrasado
                  </button>
                )}
                {alertas.resumo_comissoes?.['5_dias_vencer'] > 0 && (
                  <button
                    type="button"
                    onClick={() => setFiltroAlertaComissao(f => f === '5_dias_vencer' ? null : '5_dias_vencer')}
                    className={`px-2 py-1 rounded-full text-xs font-medium bg-blue-400 text-black transition hover:opacity-90 ${filtroAlertaComissao === '5_dias_vencer' ? 'ring-2 ring-offset-1 ring-blue-300' : filtroAlertaComissao ? 'opacity-40' : ''}`}
                    data-testid="filtro-comissao-5_dias_vencer"
                  >
                    {alertas.resumo_comissoes['5_dias_vencer']} em 5 dias
                  </button>
                )}
                {filtroAlertaComissao && (
                  <button
                    type="button"
                    onClick={() => setFiltroAlertaComissao(null)}
                    className="px-2 py-1 rounded-full text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                    data-testid="filtro-comissao-limpar"
                  >
                    ✕ Limpar
                  </button>
                )}
              </div>
            </div>
            
            {/* Lista de comissões (filtrada) */}
            <div className="max-h-[400px] overflow-y-auto divide-y divide-border" data-testid="lista-alertas-comissoes">
              {(() => {
                const lista = (alertas.alertas_comissoes || []).filter(a => !filtroAlertaComissao || a.urgencia === filtroAlertaComissao);
                if (lista.length === 0) {
                  return (
                    <p className="text-center text-sm text-muted-foreground py-8 italic">
                      Nenhum alerta {filtroAlertaComissao ? 'nesse filtro' : ''}.
                    </p>
                  );
                }
                return lista.map((alerta) => (
                <div
                  key={alerta.id}
                  onClick={() => setAlertaDetalhe(alerta)}
                  className={`p-3 cursor-pointer hover:bg-accent/50 transition-all ${
                    alerta.urgencia === 'urgente_atrasado' ? 'bg-purple-500/10' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {alerta.urgencia === 'urgente_atrasado' && (
                        <XCircle className="w-5 h-5 text-purple-500" />
                      )}
                      <div>
                        <p className="font-medium text-sm">{alerta.vendedor_nome}</p>
                        <p className="text-xs text-muted-foreground">
                          Cliente: {alerta.cliente_nome} | {alerta.percentual_comissao}%
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-sm text-purple-500">
                        {formatCurrency(alerta.comissao_pendente)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        de {formatCurrency(alerta.comissao_total)}
                      </p>
                    </div>
                  </div>
                </div>
                ));
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Vendas por Tipo de Unidade - DETALHADO */}
      {secoes.tiposUnidade && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border">
            <h3 className="font-semibold flex items-center gap-2">
              <Box className="w-5 h-5 text-primary" />
              Vendas por Tipo de Unidade
              <span className="text-xs text-muted-foreground ml-2">(Clique para ver produtos)</span>
            </h3>
          </div>
          
          <div className="p-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Cards por Tipo - Clicáveis */}
              <div className="space-y-2">
                {[
                  { tipo: 'milheiro', label: 'Milheiro (1000)', cor: 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' },
                  { tipo: 'centena', label: 'Centena (100)', cor: 'border-green-500 bg-green-50 dark:bg-green-900/20' },
                  { tipo: 'unidade', label: 'Unidade', cor: 'border-orange-500 bg-orange-50 dark:bg-orange-900/20' },
                  { tipo: 'caixa', label: 'Caixa', cor: 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' },
                  { tipo: 'pacote', label: 'Pacote', cor: 'border-pink-500 bg-pink-50 dark:bg-pink-900/20' }
                ].map(({ tipo, label, cor }) => {
                  const data = dados?.vendas_por_tipo?.[tipo] || { quantidade: 0, valor: 0, itens: 0 };
                  const isSelected = tipoDetalhe === tipo;
                  const produtos = produtosPorTipo(tipo);
                  
                  return (
                    <div key={tipo}>
                      <button
                        onClick={() => setTipoDetalhe(isSelected ? null : tipo)}
                        className={`w-full text-left border-l-4 ${cor} rounded-r-lg p-3 transition-all hover:shadow-md ${isSelected ? 'ring-2 ring-primary' : ''}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-medium">{label}</span>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {data.quantidade.toLocaleString('pt-BR')} unid. vendidas • {data.itens} itens
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-lg font-bold">{formatCurrency(data.valor)}</span>
                            <div className="text-xs text-muted-foreground">
                              {produtos.length} produtos
                            </div>
                          </div>
                          {isSelected ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
                        </div>
                      </button>
                      
                      {/* Lista de Produtos deste Tipo */}
                      {isSelected && produtos.length > 0 && (
                        <div className="mt-2 ml-4 bg-accent/30 rounded-lg p-3 max-h-60 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-xs text-muted-foreground">
                                <th className="pb-2">Produto</th>
                                <th className="pb-2 text-right">Qtd</th>
                                <th className="pb-2 text-right">Valor</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                              {produtos.map((prod, idx) => (
                                <tr key={idx} className="hover:bg-accent/50">
                                  <td className="py-1.5 font-medium">{prod.nome}</td>
                                  <td className="py-1.5 text-right">{prod.quantidade.toLocaleString('pt-BR')}</td>
                                  <td className="py-1.5 text-right font-semibold text-green-600">{formatCurrency(prod.valor)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Gráfico Pizza */}
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={dadosTipos}
                      dataKey="valor"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {dadosTipos.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Forma de Pagamento */}
      {secoes.formaPagamento && dados?.vendas_forma_pagamento && (
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="font-semibold flex items-center gap-2 mb-4">
            <CreditCard className="w-5 h-5 text-primary" />
            Vendas por Forma de Pagamento
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {Object.entries(dados.vendas_forma_pagamento).map(([forma, data]) => (
              <div key={forma} className="bg-accent/30 rounded-lg p-3 text-center">
                <p className="text-xs font-medium text-muted-foreground capitalize">{forma.replace('_', ' ')}</p>
                <p className="text-lg font-bold mt-1">{formatCurrency(data.valor)}</p>
                <p className="text-xs text-muted-foreground">{data.quantidade} pedidos</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Evolução Diária */}
      {secoes.evolucaoDiaria && dadosDiarios.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="font-semibold flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-primary" />
            Evolução de Vendas no Período
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dadosDiarios}>
                <defs>
                  <linearGradient id="colorValor" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="data" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(value) => `R$${(value/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Area type="monotone" dataKey="valor" stroke="#3B82F6" fillOpacity={1} fill="url(#colorValor)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Grid de Tabelas - 2 colunas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top Vendedores */}
        {secoes.vendedores && (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="p-3 border-b border-border bg-muted/30">
              <h3 className="font-semibold flex items-center gap-2 text-sm">
                <UserCog className="w-4 h-4 text-primary" />
                Top 10 Vendedores
              </h3>
            </div>
            <div className="overflow-x-auto max-h-80">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">#</th>
                    <th className="px-3 py-2 text-left font-medium">Vendedor</th>
                    <th className="px-3 py-2 text-right font-medium">Vendas</th>
                    <th className="px-3 py-2 text-right font-medium">Comissão</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {dados?.top_10_vendedores?.map((v, i) => (
                    <tr key={i} className="hover:bg-accent/30">
                      <td className="px-3 py-2 font-bold text-primary">{i + 1}º</td>
                      <td className="px-3 py-2 font-medium truncate max-w-[150px]">{v.nome || 'Sem vendedor'}</td>
                      <td className="px-3 py-2 text-right font-semibold text-green-600">{formatCurrency(v.total_vendas)}</td>
                      <td className="px-3 py-2 text-right text-orange-600">{formatCurrency(v.comissao_devida || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Top Clientes */}
        {secoes.clientes && (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="p-3 border-b border-border bg-muted/30">
              <h3 className="font-semibold flex items-center gap-2 text-sm">
                <Users className="w-4 h-4 text-primary" />
                Top 10 Clientes
              </h3>
            </div>
            <div className="overflow-x-auto max-h-80">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">#</th>
                    <th className="px-3 py-2 text-left font-medium">Cliente</th>
                    <th className="px-3 py-2 text-right font-medium">Compras</th>
                    <th className="px-3 py-2 text-center font-medium">Ped.</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {dados?.top_10_clientes?.map((c, i) => (
                    <tr key={i} className="hover:bg-accent/30">
                      <td className="px-3 py-2 font-bold text-primary">{i + 1}º</td>
                      <td className="px-3 py-2 font-medium truncate max-w-[200px]" title={c.nome}>{c.nome}</td>
                      <td className="px-3 py-2 text-right font-semibold text-green-600">{formatCurrency(c.total_compras)}</td>
                      <td className="px-3 py-2 text-center">{c.romaneios}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ===== SEÇÃO: DEVEDORES E ROMANEIOS ===== */}
      {(() => {
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        const fimSemana = new Date(hoje);
        fimSemana.setDate(fimSemana.getDate() + 7);

        const getDataVencimento = (r) => {
          if (r.data_vencimento_boleto) return new Date(r.data_vencimento_boleto);
          if (r.parcelas_prazo && r.parcelas_prazo.length > 0) {
            // pega a 1ª parcela com data
            const proxima = r.parcelas_prazo.find(p => p);
            if (proxima) return new Date(proxima);
          }
          if (r.dias_para_receber && r.created_at) {
            const base = new Date(r.created_at);
            base.setDate(base.getDate() + parseInt(r.dias_para_receber));
            return base;
          }
          return null;
        };

        const isPago = (r) => r.status_pagamento === 'pago' || (r.forma_pagamento || '').toLowerCase() === 'pago';

        // Devedores: agrega clientes com romaneios em aberto
        const devedoresMap = {};
        allRomaneios.forEach(r => {
          if (isPago(r)) return;
          const cliente = (r.cliente_nome || '').trim();
          if (!cliente) return;
          const saldo = (r.valor_total || 0) - (r.valor_pago || 0);
          if (saldo <= 0.01) return;
          if (!devedoresMap[cliente]) {
            devedoresMap[cliente] = { cliente, total: 0, qtd: 0, romaneios: [] };
          }
          devedoresMap[cliente].total += saldo;
          devedoresMap[cliente].qtd += 1;
          devedoresMap[cliente].romaneios.push(r);
        });
        const devedores = Object.values(devedoresMap).sort((a, b) => b.total - a.total);
        const totalDevedores = devedores.reduce((s, d) => s + d.total, 0);

        // Romaneios atrasados (vencimento < hoje, não pagos)
        const atrasados = allRomaneios.filter(r => {
          if (isPago(r)) return false;
          const venc = getDataVencimento(r);
          return venc && venc < hoje;
        }).sort((a, b) => (getDataVencimento(a) || 0) - (getDataVencimento(b) || 0));

        // Romaneios a vencer esta semana (hoje <= venc <= +7d)
        const proximos = allRomaneios.filter(r => {
          if (isPago(r)) return false;
          const venc = getDataVencimento(r);
          return venc && venc >= hoje && venc <= fimSemana;
        }).sort((a, b) => (getDataVencimento(a) || 0) - (getDataVencimento(b) || 0));

        // Últimos 10 romaneios criados
        const ultimos = [...allRomaneios]
          .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))
          .slice(0, 10);

        const fmtDt = (d) => d ? d.toLocaleDateString('pt-BR') : '-';
        const corStatus = (r) => isPago(r) ? 'text-green-600' : ((getDataVencimento(r) && getDataVencimento(r) < hoje) ? 'text-red-600' : 'text-orange-600');
        const txtStatus = (r) => {
          if (isPago(r)) return '✓ Pago';
          const venc = getDataVencimento(r);
          if (venc && venc < hoje) return '⚠️ Atrasado';
          return 'A receber';
        };

        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Card: TODOS os Devedores */}
            <div className="bg-card border-2 border-red-300 dark:border-red-700 rounded-lg overflow-hidden" data-testid="card-todos-devedores">
              <div className="px-4 py-3 bg-red-50 dark:bg-red-900/20 border-b border-red-200 flex items-center justify-between">
                <h3 className="font-bold flex items-center gap-2 text-red-700 dark:text-red-300">
                  <AlertCircle className="w-5 h-5" />
                  Todos os Devedores ({devedores.length})
                </h3>
                <div className="text-right">
                  <p className="text-[10px] text-muted-foreground">Total devido</p>
                  <p className="text-base font-bold text-red-700 dark:text-red-300">{formatCurrency(totalDevedores)}</p>
                </div>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {devedores.length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">
                    <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-500 opacity-50" />
                    Sem devedores no momento
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs">#</th>
                        <th className="px-3 py-2 text-left text-xs">Cliente</th>
                        <th className="px-3 py-2 text-center text-xs">Pedidos</th>
                        <th className="px-3 py-2 text-right text-xs">Total devido</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {devedores.map((d, idx) => (
                        <tr key={d.cliente} className="hover:bg-accent/30" data-testid={`devedor-row-${idx}`}>
                          <td className="px-3 py-2 text-xs text-muted-foreground">{idx + 1}</td>
                          <td className="px-3 py-2 font-medium truncate max-w-[200px]">{d.cliente}</td>
                          <td className="px-3 py-2 text-center text-xs">{d.qtd}</td>
                          <td className="px-3 py-2 text-right font-bold text-red-600">{formatCurrency(d.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Card: Romaneios Atrasados */}
            <div className="bg-card border-2 border-orange-300 dark:border-orange-700 rounded-lg overflow-hidden" data-testid="card-atrasados">
              <div className="px-4 py-3 bg-orange-50 dark:bg-orange-900/20 border-b border-orange-200 flex items-center justify-between">
                <h3 className="font-bold flex items-center gap-2 text-orange-700 dark:text-orange-300">
                  <AlertTriangle className="w-5 h-5" />
                  Romaneios Atrasados ({atrasados.length})
                </h3>
                <div className="text-right">
                  <p className="text-[10px] text-muted-foreground">Total</p>
                  <p className="text-base font-bold text-orange-700 dark:text-orange-300">
                    {formatCurrency(atrasados.reduce((s, r) => s + ((r.valor_total || 0) - (r.valor_pago || 0)), 0))}
                  </p>
                </div>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {atrasados.length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">
                    <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-500 opacity-50" />
                    Nenhum romaneio atrasado
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs">Nº</th>
                        <th className="px-3 py-2 text-left text-xs">Cliente</th>
                        <th className="px-3 py-2 text-left text-xs">Venceu</th>
                        <th className="px-3 py-2 text-right text-xs">Valor</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {atrasados.map((r) => (
                        <tr key={r.id} className="hover:bg-accent/30">
                          <td className="px-3 py-2 font-medium">{r.numero_pedido || '-'}</td>
                          <td className="px-3 py-2 truncate max-w-[140px]">{r.cliente_nome || '-'}</td>
                          <td className="px-3 py-2 text-xs text-red-600 font-medium">{fmtDt(getDataVencimento(r))}</td>
                          <td className="px-3 py-2 text-right font-bold text-orange-600">{formatCurrency(r.valor_total || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Card: A vencer essa semana */}
            <div className="bg-card border-2 border-yellow-300 dark:border-yellow-700 rounded-lg overflow-hidden" data-testid="card-a-vencer">
              <div className="px-4 py-3 bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 flex items-center justify-between">
                <h3 className="font-bold flex items-center gap-2 text-yellow-700 dark:text-yellow-300">
                  <Clock className="w-5 h-5" />
                  A Vencer Essa Semana ({proximos.length})
                </h3>
                <div className="text-right">
                  <p className="text-[10px] text-muted-foreground">Total</p>
                  <p className="text-base font-bold text-yellow-700 dark:text-yellow-300">
                    {formatCurrency(proximos.reduce((s, r) => s + ((r.valor_total || 0) - (r.valor_pago || 0)), 0))}
                  </p>
                </div>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {proximos.length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">
                    <Calendar className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    Nada vence nos próximos 7 dias
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs">Nº</th>
                        <th className="px-3 py-2 text-left text-xs">Cliente</th>
                        <th className="px-3 py-2 text-left text-xs">Vence</th>
                        <th className="px-3 py-2 text-right text-xs">Valor</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {proximos.map((r) => (
                        <tr key={r.id} className="hover:bg-accent/30">
                          <td className="px-3 py-2 font-medium">{r.numero_pedido || '-'}</td>
                          <td className="px-3 py-2 truncate max-w-[140px]">{r.cliente_nome || '-'}</td>
                          <td className="px-3 py-2 text-xs text-yellow-700 font-medium">{fmtDt(getDataVencimento(r))}</td>
                          <td className="px-3 py-2 text-right font-bold text-yellow-700">{formatCurrency(r.valor_total || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Card: Últimos romaneios criados */}
            <div className="bg-card border-2 border-blue-300 dark:border-blue-700 rounded-lg overflow-hidden" data-testid="card-ultimos-romaneios">
              <div className="px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 flex items-center justify-between">
                <h3 className="font-bold flex items-center gap-2 text-blue-700 dark:text-blue-300">
                  <FileText className="w-5 h-5" />
                  Últimos Romaneios ({ultimos.length})
                </h3>
                <div className="text-right">
                  <p className="text-[10px] text-muted-foreground">Total no período</p>
                  <p className="text-base font-bold text-blue-700 dark:text-blue-300">
                    {formatCurrency(ultimos.reduce((s, r) => s + (r.valor_total || 0), 0))}
                  </p>
                </div>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {ultimos.length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">Nenhum romaneio recente</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs">Nº</th>
                        <th className="px-3 py-2 text-left text-xs">Cliente</th>
                        <th className="px-3 py-2 text-left text-xs">Status</th>
                        <th className="px-3 py-2 text-right text-xs">Valor</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {ultimos.map((r) => (
                        <tr key={r.id} className="hover:bg-accent/30">
                          <td className="px-3 py-2 font-medium">{r.numero_pedido || '-'}</td>
                          <td className="px-3 py-2 truncate max-w-[140px]">{r.cliente_nome || '-'}</td>
                          <td className={`px-3 py-2 text-xs font-medium ${corStatus(r)}`}>{txtStatus(r)}</td>
                          <td className="px-3 py-2 text-right font-bold text-blue-700">{formatCurrency(r.valor_total || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ===== PAINEL: VENCIDOS DO MÊS (com filtros + ações) ===== */}
      {(() => {
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        const getDataVencimento = (r) => {
          if (r.data_vencimento_boleto) return new Date(r.data_vencimento_boleto);
          if (r.parcelas_prazo && r.parcelas_prazo.length > 0) {
            const proxima = r.parcelas_prazo.find(p => p);
            if (proxima) return new Date(proxima);
          }
          if (r.dias_para_receber && r.created_at) {
            const base = new Date(r.created_at);
            base.setDate(base.getDate() + parseInt(r.dias_para_receber));
            return base;
          }
          return null;
        };
        const isPago = (r) => r.status_pagamento === 'pago' || (r.forma_pagamento || '').toLowerCase() === 'pago';

        // Constrói lista bruta de vencidos (vencimento < hoje, não pago)
        const baseVencidos = allRomaneios
          .filter(r => !isPago(r))
          .map(r => {
            const venc = getDataVencimento(r);
            if (!venc) return null;
            if (venc >= hoje) return null;
            const diffMs = hoje.getTime() - venc.getTime();
            const diasAtraso = Math.floor(diffMs / (1000 * 60 * 60 * 24));
            const saldo = (r.valor_total || 0) - (r.valor_pago || 0);
            return {
              ...r,
              _venc: venc,
              _vencimentoStr: venc.toLocaleDateString('pt-BR'),
              _diasAtraso: diasAtraso,
              _saldo: saldo,
            };
          })
          .filter(Boolean);

        // Aplica filtros
        const dIni = vencidosFiltro.dataInicio ? new Date(vencidosFiltro.dataInicio) : null;
        const dFim = vencidosFiltro.dataFim ? new Date(vencidosFiltro.dataFim) : null;
        if (dFim) dFim.setHours(23, 59, 59, 999);
        const valMin = parseFloat(vencidosFiltro.valorMin) || null;
        const valMax = parseFloat(vencidosFiltro.valorMax) || null;
        const termoCli = vencidosFiltro.cliente.trim().toLowerCase();
        const prazoSel = vencidosFiltro.prazo;
        const faixaSel = vencidosFiltro.faixaAtraso;

        const filtrados = baseVencidos.filter(r => {
          if (dIni && r._venc < dIni) return false;
          if (dFim && r._venc > dFim) return false;
          if (termoCli && !(r.cliente_nome || '').toLowerCase().includes(termoCli)) return false;
          if (valMin !== null && r._saldo < valMin) return false;
          if (valMax !== null && r._saldo > valMax) return false;
          if (prazoSel) {
            const f = (r.forma_pagamento || '').toLowerCase();
            if (prazoSel === 'a_prazo') {
              if (!['a_prazo', 'a prazo', 'a-prazo', 'a_receber', 'a receber'].includes(f) && !(r.dias_para_receber && r.dias_para_receber > 0)) return false;
            } else if (prazoSel === 'parcelado') {
              if (!(Array.isArray(r.parcelas_prazo) && r.parcelas_prazo.length > 0)) return false;
            } else if (prazoSel === 'boleto') {
              if (!f.includes('boleto') && !r.data_vencimento_boleto) return false;
            } else if (prazoSel === 'pix') {
              if (!f.includes('pix')) return false;
            } else if (prazoSel === 'dinheiro') {
              if (!f.includes('dinheiro') && !f.includes('cash')) return false;
            }
          }
          if (faixaSel) {
            const d = r._diasAtraso;
            if (faixaSel === '1-7' && !(d >= 1 && d <= 7)) return false;
            if (faixaSel === '8-15' && !(d >= 8 && d <= 15)) return false;
            if (faixaSel === '16-30' && !(d >= 16 && d <= 30)) return false;
            if (faixaSel === '30+' && !(d > 30)) return false;
          }
          return true;
        });

        // Ordenação
        const ord = vencidosFiltro.ordenar;
        filtrados.sort((a, b) => {
          if (ord === 'atraso_desc') return b._diasAtraso - a._diasAtraso;
          if (ord === 'atraso_asc') return a._diasAtraso - b._diasAtraso;
          if (ord === 'valor_desc') return b._saldo - a._saldo;
          if (ord === 'cliente') return (a.cliente_nome || '').localeCompare(b.cliente_nome || '');
          return 0;
        });

        const totalSaldo = filtrados.reduce((s, r) => s + r._saldo, 0);
        const corAtraso = (d) => d > 30 ? 'text-red-700 bg-red-100 dark:bg-red-900/40' : d > 15 ? 'text-red-600 bg-red-50 dark:bg-red-900/30' : d > 7 ? 'text-orange-600 bg-orange-50 dark:bg-orange-900/30' : 'text-amber-600 bg-amber-50 dark:bg-amber-900/30';

        const limparFiltros = () => setVencidosFiltro({
          dataInicio: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
          dataFim: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
          cliente: '', valorMin: '', valorMax: '', prazo: '', faixaAtraso: '', ordenar: 'atraso_desc',
        });

        return (
          <div className="bg-card border-2 border-red-300 dark:border-red-700 rounded-xl overflow-hidden shadow-sm" data-testid="painel-vencidos-mes">
            {/* Cabeçalho */}
            <div className="px-5 py-4 bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-950/40 dark:to-orange-950/40 border-b border-red-200 dark:border-red-800 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-700 dark:text-red-300" />
                </div>
                <div>
                  <h3 className="font-bold text-base text-red-800 dark:text-red-200">Vencidos do Mês</h3>
                  <p className="text-xs text-red-700/70 dark:text-red-300/70">
                    {filtrados.length} romaneio(s) • {formatCurrency(totalSaldo)} em aberto
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => exportarVencidosCSV(filtrados)}
                  className="px-3 py-1.5 rounded-lg border border-red-300 dark:border-red-700 bg-white dark:bg-card text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 text-xs font-medium flex items-center gap-1.5"
                  data-testid="vencidos-export-csv"
                >
                  <FileText className="w-3.5 h-3.5" /> Exportar CSV
                </button>
                <button
                  onClick={limparFiltros}
                  className="px-3 py-1.5 rounded-lg border border-border bg-white dark:bg-card hover:bg-accent text-xs font-medium flex items-center gap-1.5"
                  data-testid="vencidos-limpar-filtros"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Limpar filtros
                </button>
              </div>
            </div>

            {/* Filtros */}
            <div className="px-5 py-3 border-b border-border bg-muted/30">
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 text-sm">
                <div>
                  <label className="block text-[10px] font-medium text-muted-foreground mb-1">Venc. de</label>
                  <input
                    type="date"
                    value={vencidosFiltro.dataInicio}
                    onChange={e => setVencidosFiltro({ ...vencidosFiltro, dataInicio: e.target.value })}
                    className="input-field !py-1.5 !text-xs"
                    data-testid="vencidos-filtro-data-inicio"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-muted-foreground mb-1">Venc. até</label>
                  <input
                    type="date"
                    value={vencidosFiltro.dataFim}
                    onChange={e => setVencidosFiltro({ ...vencidosFiltro, dataFim: e.target.value })}
                    className="input-field !py-1.5 !text-xs"
                    data-testid="vencidos-filtro-data-fim"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[10px] font-medium text-muted-foreground mb-1">Cliente</label>
                  <input
                    type="text"
                    placeholder="Buscar por nome..."
                    value={vencidosFiltro.cliente}
                    onChange={e => setVencidosFiltro({ ...vencidosFiltro, cliente: e.target.value })}
                    className="input-field !py-1.5 !text-xs"
                    data-testid="vencidos-filtro-cliente"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-muted-foreground mb-1">Saldo mín. (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0,00"
                    value={vencidosFiltro.valorMin}
                    onChange={e => setVencidosFiltro({ ...vencidosFiltro, valorMin: e.target.value })}
                    className="input-field !py-1.5 !text-xs"
                    data-testid="vencidos-filtro-valor-min"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-muted-foreground mb-1">Saldo máx. (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="9999,00"
                    value={vencidosFiltro.valorMax}
                    onChange={e => setVencidosFiltro({ ...vencidosFiltro, valorMax: e.target.value })}
                    className="input-field !py-1.5 !text-xs"
                    data-testid="vencidos-filtro-valor-max"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-muted-foreground mb-1">Prazo</label>
                  <select
                    value={vencidosFiltro.prazo}
                    onChange={e => setVencidosFiltro({ ...vencidosFiltro, prazo: e.target.value })}
                    className="input-field !py-1.5 !text-xs"
                    data-testid="vencidos-filtro-prazo"
                  >
                    <option value="">Todos</option>
                    <option value="a_prazo">A prazo / A receber</option>
                    <option value="parcelado">Parcelado</option>
                    <option value="boleto">Boleto</option>
                    <option value="pix">PIX</option>
                    <option value="dinheiro">Dinheiro</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm mt-2">
                <div>
                  <label className="block text-[10px] font-medium text-muted-foreground mb-1">Tempo de atraso</label>
                  <select
                    value={vencidosFiltro.faixaAtraso}
                    onChange={e => setVencidosFiltro({ ...vencidosFiltro, faixaAtraso: e.target.value })}
                    className="input-field !py-1.5 !text-xs"
                    data-testid="vencidos-filtro-faixa"
                  >
                    <option value="">Qualquer</option>
                    <option value="1-7">1 a 7 dias</option>
                    <option value="8-15">8 a 15 dias</option>
                    <option value="16-30">16 a 30 dias</option>
                    <option value="30+">Mais de 30 dias</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-muted-foreground mb-1">Ordenar por</label>
                  <select
                    value={vencidosFiltro.ordenar}
                    onChange={e => setVencidosFiltro({ ...vencidosFiltro, ordenar: e.target.value })}
                    className="input-field !py-1.5 !text-xs"
                    data-testid="vencidos-filtro-ordenar"
                  >
                    <option value="atraso_desc">Mais atrasados primeiro</option>
                    <option value="atraso_asc">Menos atrasados primeiro</option>
                    <option value="valor_desc">Maior saldo primeiro</option>
                    <option value="cliente">Cliente (A-Z)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Lista */}
            <div className="max-h-[28rem] overflow-y-auto">
              {filtrados.length === 0 ? (
                <div className="p-10 text-center">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-60" />
                  <p className="font-medium text-sm">Nenhum romaneio vencido no filtro aplicado</p>
                  <p className="text-xs text-muted-foreground mt-1">Ajuste os filtros acima ou limpe-os para ver mais resultados.</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold">Pedido</th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold">Cliente</th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold">Venc.</th>
                      <th className="px-3 py-2 text-center text-[11px] font-semibold">Atraso</th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold">Forma</th>
                      <th className="px-3 py-2 text-right text-[11px] font-semibold">Total</th>
                      <th className="px-3 py-2 text-right text-[11px] font-semibold">Pago</th>
                      <th className="px-3 py-2 text-right text-[11px] font-semibold">Saldo</th>
                      <th className="px-3 py-2 text-center text-[11px] font-semibold">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filtrados.map(r => (
                      <tr key={r.id} className="hover:bg-accent/30" data-testid={`vencido-row-${r.numero_pedido}`}>
                        <td className="px-3 py-2 font-semibold">#{r.numero_pedido || '-'}</td>
                        <td className="px-3 py-2 truncate max-w-[200px]">{r.cliente_nome || '-'}</td>
                        <td className="px-3 py-2 text-xs text-red-600 whitespace-nowrap">{r._vencimentoStr}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${corAtraso(r._diasAtraso)}`}>
                            {r._diasAtraso}d
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[110px]">{r.forma_pagamento || '-'}</td>
                        <td className="px-3 py-2 text-right text-xs">{formatCurrency(r.valor_total || 0)}</td>
                        <td className="px-3 py-2 text-right text-xs text-green-600">{formatCurrency(r.valor_pago || 0)}</td>
                        <td className="px-3 py-2 text-right font-bold text-red-600">{formatCurrency(r._saldo)}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => abrirPagamentoVencido(r)}
                              disabled={vencidosPayLoading[r.id]}
                              className="p-1.5 rounded hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600 disabled:opacity-50"
                              title="Pagar / Adicionar nota / Mudar status"
                              data-testid={`vencido-pagar-${r.numero_pedido}`}
                            >
                              {vencidosPayLoading[r.id]
                                ? <RefreshCw className="w-4 h-4 animate-spin" />
                                : <DollarSign className="w-4 h-4" />}
                            </button>
                            <a
                              href={`/dashboardadminromaneio/romaneios?focus=${r.id}`}
                              className="p-1.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600"
                              title="Abrir no Romaneios"
                              data-testid={`vencido-abrir-${r.numero_pedido}`}
                            >
                              <Eye className="w-4 h-4" />
                            </a>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {filtrados.length > 0 && (
                    <tfoot className="bg-red-50 dark:bg-red-900/20 border-t-2 border-red-300 dark:border-red-700 sticky bottom-0">
                      <tr>
                        <td colSpan="7" className="px-3 py-2 text-right font-bold text-xs">TOTAL EM ABERTO ({filtrados.length}):</td>
                        <td className="px-3 py-2 text-right font-bold text-red-700 dark:text-red-300">{formatCurrency(totalSaldo)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              )}
            </div>
          </div>
        );
      })()}

      {/* Top Produtos - Tabela completa */}
      {secoes.produtos && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="p-3 border-b border-border bg-muted/30">
            <h3 className="font-semibold flex items-center gap-2 text-sm">
              <Package className="w-4 h-4 text-primary" />
              Top 10 Produtos Mais Vendidos
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">#</th>
                  <th className="px-3 py-2 text-left font-medium">Produto</th>
                  <th className="px-3 py-2 text-right font-medium">Total</th>
                  <th className="px-3 py-2 text-right font-medium">Milheiro</th>
                  <th className="px-3 py-2 text-right font-medium">Centena</th>
                  <th className="px-3 py-2 text-right font-medium">Unidade</th>
                  <th className="px-3 py-2 text-right font-medium">Caixa</th>
                  <th className="px-3 py-2 text-right font-medium">Pacote</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {dados?.top_10_produtos?.map((p, i) => (
                  <tr key={i} className="hover:bg-accent/30">
                    <td className="px-3 py-2 font-bold text-primary">{i + 1}º</td>
                    <td className="px-3 py-2 font-medium">{p.nome}</td>
                    <td className="px-3 py-2 text-right font-bold text-green-600">{formatCurrency(p.total_vendas)}</td>
                    <td className="px-3 py-2 text-right text-blue-600">{p.por_tipo?.milheiro > 0 ? formatCurrency(p.por_tipo.milheiro) : '-'}</td>
                    <td className="px-3 py-2 text-right text-green-600">{p.por_tipo?.centena > 0 ? formatCurrency(p.por_tipo.centena) : '-'}</td>
                    <td className="px-3 py-2 text-right text-orange-600">{p.por_tipo?.unidade > 0 ? formatCurrency(p.por_tipo.unidade) : '-'}</td>
                    <td className="px-3 py-2 text-right text-purple-600">{p.por_tipo?.caixa > 0 ? formatCurrency(p.por_tipo.caixa) : '-'}</td>
                    <td className="px-3 py-2 text-right text-pink-600">{p.por_tipo?.pacote > 0 ? formatCurrency(p.por_tipo.pacote) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Comissões Detalhadas */}
      {secoes.comissoes && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="p-3 border-b border-border bg-muted/30">
            <h3 className="font-semibold flex items-center gap-2 text-sm">
              <Percent className="w-4 h-4 text-primary" />
              Comissões dos Vendedores
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Vendedor</th>
                  <th className="px-3 py-2 text-right font-medium">Vendas</th>
                  <th className="px-3 py-2 text-center font-medium">%</th>
                  <th className="px-3 py-2 text-right font-medium">Comissão Devida</th>
                  <th className="px-3 py-2 text-right font-medium">Pago</th>
                  <th className="px-3 py-2 text-right font-medium">Pendente</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {dados?.top_10_vendedores?.filter(v => v.percentual_comissao > 0).map((v, i) => (
                  <tr key={i} className="hover:bg-accent/30">
                    <td className="px-3 py-2 font-medium">{v.nome}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(v.total_vendas)}</td>
                    <td className="px-3 py-2 text-center">{v.percentual_comissao}%</td>
                    <td className="px-3 py-2 text-right font-semibold text-orange-600">{formatCurrency(v.comissao_devida || 0)}</td>
                    <td className="px-3 py-2 text-right text-green-600">{formatCurrency(v.valores_pagos || 0)}</td>
                    <td className="px-3 py-2 text-right font-bold text-red-600">{formatCurrency(v.valores_pendentes || 0)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-muted/30">
                <tr>
                  <td colSpan="3" className="px-3 py-2 font-bold text-right">TOTAL:</td>
                  <td className="px-3 py-2 text-right font-bold text-orange-600">{formatCurrency(totalComissaoDevida)}</td>
                  <td className="px-3 py-2 text-right font-bold text-green-600">
                    {formatCurrency(dados?.top_10_vendedores?.reduce((acc, v) => acc + (v.valores_pagos || 0), 0) || 0)}
                  </td>
                  <td className="px-3 py-2 text-right font-bold text-red-600">
                    {formatCurrency(dados?.top_10_vendedores?.reduce((acc, v) => acc + (v.valores_pendentes || 0), 0) || 0)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* ============ MODAL DE DETALHES DO ALERTA ============ */}
      {alertaDetalhe && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setAlertaDetalhe(null)}>
          <div 
            className={`bg-card rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-in zoom-in-95 ${
              alertaDetalhe.urgencia === 'urgente_atrasado' ? 'ring-4 ring-red-500' : ''
            }`}
            onClick={e => e.stopPropagation()}
          >
            {/* Header com cor de urgência */}
            <div 
              className="p-4 border-b border-border flex items-center justify-between sticky top-0 z-10"
              style={{ backgroundColor: `${alertaDetalhe.cor}20` }}
            >
              <div className="flex items-center gap-3">
                {alertaDetalhe.urgencia === 'urgente_atrasado' && (
                  <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center">
                    <XCircle className="w-6 h-6 text-white" />
                  </div>
                )}
                <div>
                  <h3 className="text-lg font-bold">
                    {alertaDetalhe.tipo === 'comissao' ? 'Comissão Pendente' : 'Pagamento Pendente'}
                  </h3>
                  <p className="text-sm" style={{ color: alertaDetalhe.cor }}>
                    {alertaDetalhe.dias_ate_vencimento < 0 
                      ? `⚠️ ${Math.abs(alertaDetalhe.dias_ate_vencimento)} dia(s) em atraso!`
                      : `${alertaDetalhe.dias_ate_vencimento} dia(s) para vencimento`
                    }
                  </p>
                </div>
              </div>
              <button onClick={() => setAlertaDetalhe(null)} className="p-2 hover:bg-accent rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Conteúdo */}
            <div className="p-4 space-y-4">
              {/* Informações do Romaneio */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">Nº Pedido</p>
                  <p className="font-bold text-lg">{alertaDetalhe.numero_pedido}</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">Data Vencimento</p>
                  <p className="font-bold text-lg">
                    {new Date(alertaDetalhe.data_vencimento).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              </div>

              {/* Cliente e Vendedor */}
              <div className="grid grid-cols-2 gap-4">
                <div className="border border-border rounded-lg p-3">
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Users className="w-3 h-3" /> Cliente
                  </p>
                  <p className="font-semibold">{alertaDetalhe.cliente_nome}</p>
                </div>
                <div className="border border-border rounded-lg p-3">
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <UserCog className="w-3 h-3" /> Vendedor
                  </p>
                  <p className="font-semibold">{alertaDetalhe.vendedor_nome || '-'}</p>
                </div>
              </div>

              {/* Valores */}
              <div className="bg-gradient-to-r from-muted/30 to-muted/50 rounded-lg p-4">
                {alertaDetalhe.tipo === 'comissao' ? (
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-xs text-muted-foreground">Valor Venda</p>
                      <p className="font-bold text-lg">{formatCurrency(alertaDetalhe.valor_venda)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Comissão ({alertaDetalhe.percentual_comissao}%)</p>
                      <p className="font-bold text-lg text-purple-500">{formatCurrency(alertaDetalhe.comissao_total)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Pendente</p>
                      <p className="font-bold text-xl text-red-500">{formatCurrency(alertaDetalhe.comissao_pendente)}</p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-xs text-muted-foreground">Valor Total</p>
                      <p className="font-bold text-lg">{formatCurrency(alertaDetalhe.valor_total)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Já Pago</p>
                      <p className="font-bold text-lg text-green-500">{formatCurrency(alertaDetalhe.valor_pago)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Pendente</p>
                      <p className="font-bold text-xl text-red-500">{formatCurrency(alertaDetalhe.valor_pendente)}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Parcelas com vencimentos */}
              {alertaDetalhe.tipo !== 'comissao' && Array.isArray(alertaDetalhe.parcelas_prazo) && alertaDetalhe.parcelas_prazo.length > 0 && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3" data-testid="parcelas-vencimentos">
                  <p className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-blue-600" />
                    Parcelas e Vencimentos:
                  </p>
                  <div className="space-y-1.5">
                    {alertaDetalhe.parcelas_prazo.map((p, idx) => {
                      const diaVenc = p.data_vencimento || p.vencimento;
                      const valorParc = p.valor || p.valor_parcela || 0;
                      const pago = p.pago || p.status === 'pago';
                      const hoje = new Date();
                      hoje.setHours(0,0,0,0);
                      const vencDate = diaVenc ? new Date(diaVenc) : null;
                      const vencido = !pago && vencDate && vencDate < hoje;
                      return (
                        <div key={idx} className={`flex items-center justify-between gap-2 text-sm rounded px-2 py-1 ${
                          pago ? 'bg-green-100 dark:bg-green-900/30' :
                          vencido ? 'bg-red-100 dark:bg-red-900/30' : 'bg-background'
                        }`}>
                          <span className="font-medium">Parcela {idx + 1}</span>
                          <span className="text-muted-foreground">
                            Venc: <strong className={vencido ? 'text-red-600' : ''}>{diaVenc ? new Date(diaVenc).toLocaleDateString('pt-BR') : '-'}</strong>
                          </span>
                          <span className="font-bold text-right">{formatCurrency(valorParc)}</span>
                          {pago ? <span className="text-xs bg-green-600 text-white px-2 rounded">Pago</span> :
                           vencido ? <span className="text-xs bg-red-600 text-white px-2 rounded">Vencida</span> :
                           <span className="text-xs bg-blue-500 text-white px-2 rounded">A vencer</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Itens do romaneio (se for pagamento de cliente) */}
              {alertaDetalhe.tipo !== 'comissao' && alertaDetalhe.items?.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Itens do Pedido:</p>
                  <div className="border border-border rounded-lg overflow-hidden max-h-40 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted">
                        <tr>
                          <th className="px-3 py-2 text-left">Produto</th>
                          <th className="px-3 py-2 text-center">Qtd</th>
                          <th className="px-3 py-2 text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {alertaDetalhe.items.map((item, idx) => (
                          <tr key={idx}>
                            <td className="px-3 py-2">{item.produto_nome}</td>
                            <td className="px-3 py-2 text-center">{item.quantidade}</td>
                            <td className="px-3 py-2 text-right">{formatCurrency(item.valor_total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Observações */}
              {alertaDetalhe.observacoes && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1">Observações:</p>
                  <p className="text-sm">{alertaDetalhe.observacoes}</p>
                </div>
              )}

              {/* Histórico de Pagamentos */}
              {((alertaDetalhe.tipo === 'comissao' && alertaDetalhe.historico_comissoes?.length > 0) ||
                (alertaDetalhe.tipo !== 'comissao' && alertaDetalhe.historico_pagamentos?.length > 0)) && (
                <div>
                  <p className="text-sm font-medium mb-2 flex items-center gap-2">
                    <History className="w-4 h-4" />
                    Histórico de Pagamentos ({alertaDetalhe.tipo === 'comissao' 
                      ? alertaDetalhe.historico_comissoes?.length 
                      : alertaDetalhe.historico_pagamentos?.length} abatimento(s))
                  </p>
                  <div className="border border-border rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-green-100 dark:bg-green-900/30 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">Data</th>
                          <th className="px-3 py-2 text-right">Valor</th>
                          <th className="px-3 py-2 text-left">Obs</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {(alertaDetalhe.tipo === 'comissao' 
                          ? alertaDetalhe.historico_comissoes 
                          : alertaDetalhe.historico_pagamentos
                        )?.map((pag, idx) => (
                          <tr key={idx} className="bg-green-50/50 dark:bg-green-900/10">
                            <td className="px-3 py-2">
                              {new Date(pag.data).toLocaleDateString('pt-BR')}
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-green-600">
                              {formatCurrency(pag.valor)}
                            </td>
                            <td className="px-3 py-2 text-xs text-muted-foreground">
                              {pag.observacao || '-'}
                            </td>
                            <td className="px-2 py-2 text-center">
                              <button
                                onClick={async () => {
                                  if (!window.confirm(`Remover pagamento de ${formatCurrency(pag.valor)}?`)) return;
                                  const endpoint = alertaDetalhe.tipo === 'comissao' ? 'comissao' : 'pagamento';
                                  try {
                                    await axios.delete(`${API}/romaneios/${alertaDetalhe.romaneio_id || alertaDetalhe.id}/${endpoint}/${idx}`);
                                    setAlertaDetalhe(null);
                                    fetchAlertas();
                                  } catch (error) {
                                    console.error('Falha ao remover pagamento/comissão:', error);
                                  }
                                }}
                                className="text-red-500 hover:text-red-700 p-0.5" title="Remover">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-muted">
                        <tr>
                          <td className="px-3 py-2 font-semibold">Total Abatido</td>
                          <td className="px-3 py-2 text-right font-bold text-green-600">
                            {formatCurrency(
                              (alertaDetalhe.tipo === 'comissao' 
                                ? alertaDetalhe.historico_comissoes 
                                : alertaDetalhe.historico_pagamentos
                              )?.reduce((sum, p) => sum + p.valor, 0) || 0
                            )}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Ações */}
            <div className="p-4 border-t border-border bg-muted/30 flex items-center justify-end gap-3">
              <button 
                onClick={() => setAlertaDetalhe(null)}
                className="btn-secondary"
              >
                Fechar
              </button>
              <button 
                onClick={() => {
                  setAlertaSelecionado(alertaDetalhe);
                  setPagamentoData({ valor: '', data: new Date().toISOString().split('T')[0], observacao: '' });
                  setAbatimentos([{ valor: '', descricao: '' }]);
                  setShowPagamentoModal(true);
                  setAlertaDetalhe(null);
                }}
                className="btn-primary flex items-center gap-2"
              >
                <Banknote className="w-4 h-4" />
                Registrar Pagamento
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============ MODAL DE REGISTRAR PAGAMENTO ============ */}
      {showPagamentoModal && alertaSelecionado && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowPagamentoModal(false)}>
          <div 
            className="bg-card rounded-lg shadow-2xl w-full max-w-lg animate-in zoom-in-95"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-4 border-b border-border">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                Registrar Pagamento
              </h3>
              <p className="text-sm text-muted-foreground">
                {alertaSelecionado.tipo === 'comissao' 
                  ? `Comissão - ${alertaSelecionado.vendedor_nome}`
                  : `Cliente - ${alertaSelecionado.cliente_nome}`
                } | Pedido #{alertaSelecionado.numero_pedido}
              </p>
            </div>

            <div className="p-4 space-y-4">
              {/* Resumo de Valores */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <p className="text-xs text-muted-foreground">Total</p>
                  <p className="font-bold text-lg">
                    {formatCurrency(alertaSelecionado.tipo === 'comissao' 
                      ? alertaSelecionado.comissao_total 
                      : alertaSelecionado.valor_total
                    )}
                  </p>
                </div>
                <div className="bg-green-100 dark:bg-green-900/30 rounded-lg p-3 text-center">
                  <p className="text-xs text-muted-foreground">Já Pago</p>
                  <p className="font-bold text-lg text-green-600">
                    {formatCurrency(alertaSelecionado.tipo === 'comissao' 
                      ? alertaSelecionado.comissao_paga 
                      : alertaSelecionado.valor_pago
                    )}
                  </p>
                </div>
                <div className="bg-red-100 dark:bg-red-900/30 rounded-lg p-3 text-center">
                  <p className="text-xs text-muted-foreground">Pendente</p>
                  <p className="font-bold text-lg text-red-600">
                    {formatCurrency(alertaSelecionado.tipo === 'comissao' 
                      ? alertaSelecionado.comissao_pendente 
                      : alertaSelecionado.valor_pendente
                    )}
                  </p>
                </div>
              </div>

              {/* Histórico anterior (se houver) */}
              {((alertaSelecionado.tipo === 'comissao' && alertaSelecionado.historico_comissoes?.length > 0) ||
                (alertaSelecionado.tipo !== 'comissao' && alertaSelecionado.historico_pagamentos?.length > 0)) && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                  <p className="text-xs font-medium text-green-700 dark:text-green-400 mb-2 flex items-center gap-1">
                    <History className="w-3 h-3" />
                    Abatimentos Anteriores
                  </p>
                  <div className="space-y-1 max-h-24 overflow-y-auto">
                    {(alertaSelecionado.tipo === 'comissao' 
                      ? alertaSelecionado.historico_comissoes 
                      : alertaSelecionado.historico_pagamentos
                    )?.map((pag, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          {new Date(pag.data).toLocaleDateString('pt-BR')}
                        </span>
                        <span className="font-semibold text-green-600">
                          {formatCurrency(pag.valor)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Formulário de novo pagamento */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Valor Pago</label>
                  <input
                    type="number"
                    step="0.01"
                    value={pagamentoData.valor}
                    onChange={e => setPagamentoData(prev => ({ ...prev, valor: e.target.value }))}
                    placeholder="0,00"
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Data do Pagamento</label>
                  <input
                    type="date"
                    value={pagamentoData.data}
                    onChange={e => setPagamentoData(prev => ({ ...prev, data: e.target.value }))}
                    className="input-field"
                  />
                </div>
              </div>

              {/* Campo de Abatimentos Múltiplos */}
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-yellow-700 dark:text-yellow-400 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Valores a Abater (opcional)
                  </p>
                  <button
                    type="button"
                    onClick={addAbatimento}
                    className="text-xs bg-yellow-200 hover:bg-yellow-300 dark:bg-yellow-800 dark:hover:bg-yellow-700 text-yellow-800 dark:text-yellow-200 px-2 py-1 rounded flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" />
                    Adicionar
                  </button>
                </div>
                
                {/* Lista de abatimentos */}
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {abatimentos.map((abatimento, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div className="w-28 flex-shrink-0">
                        <input
                          type="number"
                          step="0.01"
                          value={abatimento.valor}
                          onChange={e => updateAbatimento(index, 'valor', e.target.value)}
                          placeholder="0,00"
                          className="input-field text-sm h-9 w-full"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <input
                          type="text"
                          value={abatimento.descricao}
                          onChange={e => updateAbatimento(index, 'descricao', e.target.value)}
                          placeholder="Descrição do abatimento"
                          className="input-field text-sm h-9 w-full"
                        />
                      </div>
                      <div className="w-8 flex-shrink-0 flex justify-center">
                        {abatimentos.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeAbatimento(index)}
                            className="p-1.5 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Preview do valor líquido */}
                {pagamentoData.valor && (
                  <div className="flex justify-between items-center pt-3 mt-3 border-t border-yellow-200 dark:border-yellow-700">
                    <div className="text-xs text-yellow-700 dark:text-yellow-400">
                      {totalAbatimentos > 0 && (
                        <span>Total Abatimentos: <strong className="text-red-600">- {formatCurrency(totalAbatimentos)}</strong></span>
                      )}
                    </div>
                    <div className="text-right">
                      <span className="text-xs text-muted-foreground">Valor Líquido: </span>
                      <span className="font-bold text-green-600">
                        {formatCurrency(Math.max(0, (parseFloat(pagamentoData.valor) || 0) - totalAbatimentos))}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Botões rápidos */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPagamentoData(prev => ({ 
                    ...prev, 
                    valor: (alertaSelecionado.tipo === 'comissao' 
                      ? alertaSelecionado.comissao_pendente 
                      : alertaSelecionado.valor_pendente
                    ).toFixed(2)
                  }))}
                  className="flex-1 text-xs py-2 bg-green-100 hover:bg-green-200 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg transition-colors"
                >
                  Quitar Total
                </button>
                <button
                  type="button"
                  onClick={() => setPagamentoData(prev => ({ 
                    ...prev, 
                    valor: ((alertaSelecionado.tipo === 'comissao' 
                      ? alertaSelecionado.comissao_pendente 
                      : alertaSelecionado.valor_pendente
                    ) / 2).toFixed(2)
                  }))}
                  className="flex-1 text-xs py-2 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-lg transition-colors"
                >
                  Metade (50%)
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Observação (opcional)</label>
                <textarea
                  value={pagamentoData.observacao}
                  onChange={e => setPagamentoData(prev => ({ ...prev, observacao: e.target.value }))}
                  placeholder="Ex: Pago via PIX"
                  className="input-field"
                  rows={2}
                />
              </div>
            </div>

            <div className="p-4 border-t border-border bg-muted/30 flex items-center justify-end gap-3">
              <button 
                onClick={() => {
                  setShowPagamentoModal(false);
                  setPagamentoData({ valor: '', data: '', observacao: '' });
                  setAbatimentos([{ valor: '', descricao: '' }]);
                }}
                className="btn-secondary"
              >
                Cancelar
              </button>
              <button 
                onClick={registrarPagamento}
                className="btn-primary"
                disabled={!pagamentoData.valor || !pagamentoData.data}
              >
                Confirmar Pagamento
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Pagamento/Nota do Painel Vencidos */}
      {pagVencidoModal.open && pagVencidoModal.romaneio && (() => {
        const rom = pagVencidoModal.romaneio;
        const valorInf = parseFloat(String(pagVencidoForm.valor).replace(',', '.')) || 0;
        const saldoAtual = Math.max(0, (rom.valor_total || 0) - (rom.valor_pago || 0));
        const saldoApos = Math.max(0, saldoAtual - valorInf);
        const seraQuitado = valorInf >= saldoAtual - 0.005 && valorInf > 0;
        const historico = rom.historico_pagamentos || [];
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            data-testid="modal-pagamento-vencido"
          >
            <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
              {/* Cabeçalho */}
              <div className="px-5 py-4 bg-gradient-to-r from-red-600 to-orange-600 text-white flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <DollarSign className="w-6 h-6" />
                  <div>
                    <h3 className="font-bold">Pagamento / Nota — #{rom.numero_pedido}</h3>
                    <p className="text-xs opacity-90">{rom.cliente_nome} • Vence {new Date(rom._venc || rom.data_vencimento_boleto || Date.now()).toLocaleDateString('pt-BR')}</p>
                  </div>
                </div>
                <button onClick={fecharPagamentoVencido} className="hover:bg-white/20 p-1 rounded" data-testid="modal-pag-vencido-fechar">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Corpo */}
              <div className="p-5 overflow-y-auto space-y-4">
                {/* Resumo de valores */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-muted/50 rounded-lg p-3">
                    <p className="text-[10px] text-muted-foreground uppercase font-semibold">Total</p>
                    <p className="text-base font-bold">{formatCurrency(rom.valor_total || 0)}</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                    <p className="text-[10px] text-green-700 dark:text-green-300 uppercase font-semibold">Já pago</p>
                    <p className="text-base font-bold text-green-700 dark:text-green-300">{formatCurrency(rom.valor_pago || 0)}</p>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3">
                    <p className="text-[10px] text-red-700 dark:text-red-300 uppercase font-semibold">Saldo devedor</p>
                    <p className="text-base font-bold text-red-700 dark:text-red-300" data-testid="pag-vencido-saldo-atual">{formatCurrency(saldoAtual)}</p>
                  </div>
                </div>

                {/* Atalhos de valor */}
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setPagVencidoForm(f => ({ ...f, valor: saldoAtual.toFixed(2).replace('.', ','), pagarTudo: true }))}
                    className="px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-medium flex items-center gap-1.5"
                    data-testid="pag-vencido-quitar"
                  >
                    <DollarSign className="w-3.5 h-3.5" /> Quitar tudo ({formatCurrency(saldoAtual)})
                  </button>
                  <button
                    type="button"
                    onClick={() => setPagVencidoForm(f => ({ ...f, valor: (saldoAtual / 2).toFixed(2).replace('.', ','), pagarTudo: false }))}
                    className="px-3 py-1.5 rounded-lg border border-border hover:bg-accent text-xs font-medium"
                  >
                    50% ({formatCurrency(saldoAtual / 2)})
                  </button>
                  <button
                    type="button"
                    onClick={() => setPagVencidoForm(f => ({ ...f, valor: '', pagarTudo: false }))}
                    className="px-3 py-1.5 rounded-lg border border-border hover:bg-accent text-xs font-medium"
                  >
                    Limpar
                  </button>
                </div>

                {/* Form */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium mb-1">Valor do pagamento (R$) *</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={pagVencidoForm.valor}
                      onChange={e => setPagVencidoForm(f => ({ ...f, valor: e.target.value.replace(/[^0-9.,]/g, ''), pagarTudo: false }))}
                      className="input-field text-lg font-semibold"
                      placeholder="0,00"
                      data-testid="pag-vencido-valor-input"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Data *</label>
                    <input
                      type="date"
                      value={pagVencidoForm.data}
                      onChange={e => setPagVencidoForm(f => ({ ...f, data: e.target.value }))}
                      className="input-field"
                      data-testid="pag-vencido-data-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1">Forma de pagamento</label>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { v: 'pix', l: 'PIX' },
                      { v: 'dinheiro', l: 'Dinheiro' },
                      { v: 'boleto', l: 'Boleto' },
                      { v: 'transferencia', l: 'Transferência' },
                      { v: 'cartao', l: 'Cartão' },
                      { v: 'cheque', l: 'Cheque' },
                    ].map(opt => (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => setPagVencidoForm(f => ({ ...f, forma: opt.v }))}
                        className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition ${pagVencidoForm.forma === opt.v ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-accent'}`}
                        data-testid={`pag-vencido-forma-${opt.v}`}
                      >
                        {opt.l}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1">Observação / Nota</label>
                  <textarea
                    value={pagVencidoForm.observacao}
                    onChange={e => setPagVencidoForm(f => ({ ...f, observacao: e.target.value }))}
                    rows={2}
                    placeholder="Ex.: cliente acertou parte por PIX, restante para próxima semana..."
                    className="input-field resize-none"
                    data-testid="pag-vencido-obs-input"
                  />
                </div>

                {/* Preview do impacto */}
                {valorInf > 0 && (
                  <div className={`rounded-lg p-3 border ${seraQuitado ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-800' : 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-800'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      {seraQuitado ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Clock className="w-4 h-4 text-amber-600" />}
                      <p className={`text-xs font-bold ${seraQuitado ? 'text-green-700 dark:text-green-300' : 'text-amber-700 dark:text-amber-300'}`}>
                        {seraQuitado ? '✓ Romaneio será QUITADO' : 'Pagamento PARCIAL'}
                      </p>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <span className="text-muted-foreground">Pagando agora:</span>
                        <p className="font-semibold">{formatCurrency(valorInf)}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Pago total ficará:</span>
                        <p className="font-semibold">{formatCurrency((rom.valor_pago || 0) + valorInf)}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Saldo restante:</span>
                        <p className={`font-bold ${seraQuitado ? 'text-green-600' : 'text-red-600'}`} data-testid="pag-vencido-saldo-apos">{formatCurrency(saldoApos)}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Histórico de pagamentos */}
                {historico.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <History className="w-3.5 h-3.5 text-muted-foreground" />
                      <p className="text-xs font-semibold text-muted-foreground uppercase">Pagamentos anteriores ({historico.length})</p>
                    </div>
                    <div className="max-h-32 overflow-y-auto rounded-lg border border-border">
                      <table className="w-full text-xs">
                        <tbody className="divide-y divide-border">
                          {historico.map((h, i) => (
                            <tr key={i} className="hover:bg-accent/30">
                              <td className="px-2 py-1.5 text-muted-foreground whitespace-nowrap">{h.data ? new Date(h.data).toLocaleDateString('pt-BR') : '-'}</td>
                              <td className="px-2 py-1.5 font-semibold text-green-700 dark:text-green-300">{formatCurrency(h.valor || 0)}</td>
                              <td className="px-2 py-1.5 truncate max-w-[200px] text-muted-foreground">{h.observacao || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* Rodapé com ações */}
              <div className="px-5 py-3 border-t border-border bg-muted/30 flex flex-wrap items-center justify-between gap-2">
                <button
                  onClick={reverterPagamentos}
                  disabled={pagVencidoBusy || (historico.length === 0 && (rom.valor_pago || 0) <= 0.01)}
                  className="px-3 py-2 rounded-lg border border-red-300 dark:border-red-800 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 text-xs font-medium disabled:opacity-40 flex items-center gap-1.5"
                  title="Remove TODOS os pagamentos e volta o romaneio para pendente / atrasado"
                  data-testid="pag-vencido-reverter"
                >
                  <XCircle className="w-3.5 h-3.5" /> Reverter p/ pendente
                </button>
                <div className="flex items-center gap-2">
                  <button
                    onClick={fecharPagamentoVencido}
                    disabled={pagVencidoBusy}
                    className="px-4 py-2 rounded-lg border border-border hover:bg-accent text-sm font-medium disabled:opacity-50"
                    data-testid="pag-vencido-cancelar"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={submitPagamentoVencido}
                    disabled={pagVencidoBusy || valorInf <= 0}
                    className={`px-4 py-2 rounded-lg text-white text-sm font-bold disabled:opacity-50 flex items-center gap-1.5 ${seraQuitado ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700'}`}
                    data-testid="pag-vencido-confirmar"
                  >
                    {pagVencidoBusy
                      ? <><RefreshCw className="w-4 h-4 animate-spin" /> Salvando...</>
                      : (seraQuitado
                          ? <><CheckCircle className="w-4 h-4" /> Quitar romaneio</>
                          : <><DollarSign className="w-4 h-4" /> Registrar pagamento</>)}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};
